import { useEffect, useMemo, useState } from 'react'
import { UserIcon, EyeIcon, StarIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline'
import {
  AML_CATEGORY_TABS,
  resolveResultsByCategory,
  groupResultsByCategory,
  formatThreatLevel
} from '../../utils/amlResultCategories'
import { getMatchCardHeadline, getMatchCardSubtitle } from '../../utils/amlMatchPresentation'
import { isWatchlistAmlProvider, sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'
import {
  filterAmlSearchResults,
  hasActiveAmlResultFilters,
  classifyNameFit,
  countNameFits
} from '../../utils/filterAmlSearchResults'
import GreenIdScreeningSummary from './GreenIdScreeningSummary'

const EMPTY_FILTERS = {
  nameMode: 'all',
  fitBand: 'all',
  excludeText: '',
  minConfidence: 0,
  hideAdverseMedia: false
}

/** Stable-enough key so Dismiss survives re-filters within one search. */
export function getAmlMatchDismissKey(match, index = 0) {
  if (!match) return `row-${index}`
  const parts = [
    match.id,
    match.profileId,
    match.greenidProfileId,
    match.entityId,
    match.primaryName || match.name || match.cardHeadline,
    match.riskCategory,
    match.matchKind,
    match.confidence
  ].filter((v) => v != null && String(v).trim() !== '')
  return parts.length ? parts.join('::') : `row-${index}`
}

/**
 * Tabbed AML search results with match cards for the active category.
 */
export default function AmlCategorizedSearchResults({
  persons = [],
  resultsByCategory = null,
  searchQuery = '',
  provider = null,
  greenidVerificationId = null,
  greenidOverallStatus = null,
  greenidProfilesMatchedCount = null,
  screeningSummary = null,
  onViewDetails,
  getRiskBadgeColor,
  getRiskLevelColor,
  getConfidenceColor,
  getThreatLevelColor
}) {
  const [resultFilters, setResultFilters] = useState(EMPTY_FILTERS)
  const [dismissedKeys, setDismissedKeys] = useState(() => new Set())

  useEffect(() => {
    setResultFilters(EMPTY_FILTERS)
    setDismissedKeys(new Set())
  }, [persons, searchQuery])

  const filtersActive = hasActiveAmlResultFilters(resultFilters)
  const dismissedCount = dismissedKeys.size
  const fitCounts = useMemo(() => countNameFits(persons, searchQuery), [persons, searchQuery])

  const visiblePersons = useMemo(() => {
    const base = filtersActive
      ? filterAmlSearchResults(persons, { ...resultFilters, searchQuery })
      : persons
    if (!dismissedCount) return base
    return base.filter((m) => !dismissedKeys.has(getAmlMatchDismissKey(m)))
  }, [persons, resultFilters, searchQuery, filtersActive, dismissedKeys, dismissedCount])

  const grouped = useMemo(() => {
    if (filtersActive || dismissedCount > 0) return groupResultsByCategory(visiblePersons)
    return resolveResultsByCategory(persons, resultsByCategory)
  }, [filtersActive, dismissedCount, visiblePersons, persons, resultsByCategory])

  const firstNonEmptyTab = useMemo(() => {
    const tab = AML_CATEGORY_TABS.find((t) => (grouped[t.key]?.length || 0) > 0)
    return tab?.key || 'pep'
  }, [grouped])

  const [activeTab, setActiveTab] = useState(firstNonEmptyTab)
  useEffect(
    () => setActiveTab(firstNonEmptyTab),
    [firstNonEmptyTab, persons, filtersActive, dismissedCount]
  )

  const activeList = grouped[activeTab] || []
  const totalCount = AML_CATEGORY_TABS.reduce((n, t) => n + (grouped[t.key]?.length || 0), 0)
  const rawTotal = Array.isArray(persons) ? persons.length : 0
  const narrowed = filtersActive || dismissedCount > 0

  function dismissMatch(match) {
    const key = getAmlMatchDismissKey(match)
    setDismissedKeys((prev) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
  }

  function restoreDismissed() {
    setDismissedKeys(new Set())
  }

  if (rawTotal === 0) return null

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center flex-wrap gap-2">
        <span className="inline-flex items-center">
          <UserIcon className="h-5 w-5 text-gray-600 mr-2" />
          Screening matches ({totalCount}
          {narrowed && rawTotal !== totalCount ? ` of ${rawTotal}` : ''})
        </span>
        {dismissedCount > 0 && (
          <button
            type="button"
            onClick={restoreDismissed}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            Restore {dismissedCount} dismissed
          </button>
        )}
      </h3>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 mr-1">Name fit</span>
        {[
          { key: 'all', label: `All (${fitCounts.total})` },
          { key: 'exact', label: `Exact fit (${fitCounts.exact})` },
          { key: 'wider', label: `Wider fit (${fitCounts.wider})` },
        ].map((band) => (
          <button
            key={band.key}
            type="button"
            onClick={() => setResultFilters((f) => ({ ...f, fitBand: band.key }))}
            className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
              (resultFilters.fitBand || 'all') === band.key
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {band.label}
          </button>
        ))}
        <p className="w-full text-[11px] text-gray-500">
          Exact = searched first and last name as whole words (middle names allowed). Wider = nickname or extra given names (e.g. Alex Hawkins vs Casey Alexander Hawkins).
        </p>
      </div>

      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center text-sm font-medium text-gray-800">
            <FunnelIcon className="h-4 w-4 mr-1.5 text-gray-500" />
            Refine results (post-search)
          </div>
          <div className="flex items-center gap-3">
            {dismissedCount > 0 && (
              <button
                type="button"
                className="text-xs text-gray-600 hover:text-gray-800"
                onClick={restoreDismissed}
              >
                Restore dismissed ({dismissedCount})
              </button>
            )}
            {filtersActive && (
              <button
                type="button"
                className="text-xs text-primary-600 hover:text-primary-700"
                onClick={() => setResultFilters(EMPTY_FILTERS)}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label htmlFor="aml-name-mode" className="block text-xs font-medium text-gray-600 mb-1">
              Name match
            </label>
            <select
              id="aml-name-mode"
              value={resultFilters.nameMode}
              onChange={(e) => setResultFilters((f) => ({ ...f, nameMode: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
            >
              <option value="all">All provider matches</option>
              <option value="all_words">Must include all search words</option>
              <option value="first_last">First + last name (middle OK)</option>
              <option value="first_last_exact">First + last only (no middle)</option>
            </select>
          </div>
          <div>
            <label htmlFor="aml-min-conf" className="block text-xs font-medium text-gray-600 mb-1">
              Min match %
            </label>
            <input
              id="aml-min-conf"
              type="number"
              min={0}
              max={100}
              step={5}
              value={resultFilters.minConfidence || ''}
              onChange={(e) =>
                setResultFilters((f) => ({
                  ...f,
                  minConfidence: e.target.value === '' ? 0 : Number(e.target.value)
                }))
              }
              placeholder="0"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="aml-exclude" className="block text-xs font-medium text-gray-600 mb-1">
              Exclude names containing
            </label>
            <input
              id="aml-exclude"
              type="text"
              value={resultFilters.excludeText}
              onChange={(e) => setResultFilters((f) => ({ ...f, excludeText: e.target.value }))}
              placeholder='e.g. William Higgs, McCurtayne'
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Comma-separated. Hides matches whose name or aliases include any phrase.
            </p>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={resultFilters.hideAdverseMedia}
            onChange={(e) =>
              setResultFilters((f) => ({ ...f, hideAdverseMedia: e.target.checked }))
            }
            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
          />
          Hide Adverse Media rows
        </label>
      </div>

      {isWatchlistAmlProvider(screeningSummary?.provider) ? (
        <GreenIdScreeningSummary summary={screeningSummary} />
      ) : (
        (greenidVerificationId || greenidProfilesMatchedCount != null || greenidOverallStatus) && (
          <div className="text-xs text-gray-500 border border-gray-200 rounded-md p-2 bg-gray-50 mb-3">
            {greenidVerificationId && (
              <span className="mr-3">
                Screening reference: <strong>{greenidVerificationId}</strong>
              </span>
            )}
            {greenidProfilesMatchedCount != null && (
              <span className="mr-3">
                Structured profiles matched: <strong>{greenidProfilesMatchedCount}</strong>
              </span>
            )}
            {greenidOverallStatus && (
              <span>
                Overall: <strong>{formatThreatLevel(greenidOverallStatus)}</strong>
              </span>
            )}
          </div>
        )
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3 mb-4">
        {AML_CATEGORY_TABS.map((tab) => {
          const count = grouped[tab.key]?.length || 0
          if (count === 0) return null
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded ${activeTab === tab.key ? 'bg-primary-500' : 'bg-gray-100'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {totalCount === 0 ? (
        <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-md px-3 py-4 bg-white">
          No matches left after filters{dismissedCount ? ' or dismissals' : ''}.
          {dismissedCount > 0 && (
            <>
              {' '}
              <button type="button" className="text-primary-600 hover:underline" onClick={restoreDismissed}>
                Restore {dismissedCount} dismissed
              </button>
            </>
          )}
          {filtersActive && (
            <>
              {' '}
              <button
                type="button"
                className="text-primary-600 hover:underline"
                onClick={() => setResultFilters(EMPTY_FILTERS)}
              >
                Clear filters
              </button>
            </>
          )}
          {' '}({rawTotal} from provider).
        </p>
      ) : (
        <div className="space-y-3">
          {activeList.map((person, idx) => (
            <PersonMatchCard
              key={`person-${getAmlMatchDismissKey(person)}-${idx}`}
              person={person}
              searchQuery={searchQuery}
              onViewDetails={onViewDetails}
              onDismiss={dismissMatch}
              getRiskBadgeColor={getRiskBadgeColor}
              getRiskLevelColor={getRiskLevelColor}
              getConfidenceColor={getConfidenceColor}
              getThreatLevelColor={getThreatLevelColor}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function PersonMatchCard({
  person,
  searchQuery = '',
  onViewDetails,
  onDismiss,
  getRiskBadgeColor,
  getRiskLevelColor,
  getConfidenceColor,
  getThreatLevelColor
}) {
  const threatDisplay = formatThreatLevel(person.threatLevel)
  const headline = getMatchCardHeadline(person)
  const cardSubtitle = getMatchCardSubtitle(person)
  const nameFit = classifyNameFit(person, searchQuery)
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">{headline}</h4>
            <span
              className={`px-2 py-1 text-xs font-medium rounded border ${
                nameFit === 'exact'
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200'
              }`}
              title={nameFit === 'exact' ? 'First and last name match as whole words' : 'Name is similar but not an exact given+family match'}
            >
              {nameFit === 'exact' ? 'Exact fit' : 'Wider fit'}
            </span>
            {person.detailKindLabel && (
              <span className="px-2 py-1 text-xs rounded border bg-slate-100 text-slate-700">
                {person.detailKindLabel}
              </span>
            )}
            <span className={`px-2 py-1 text-xs font-medium rounded border ${getRiskBadgeColor(person.riskCategory)}`}>
              {person.riskCategory}
            </span>
            {person.riskLevel && (
              <span className={`px-2 py-1 text-xs font-bold rounded border ${getRiskLevelColor(person.riskLevel)}`}>
                {person.riskLevel}
              </span>
            )}
            <div className={`flex items-center text-sm font-medium ${getConfidenceColor(person.confidence)}`}>
              <StarIcon className="h-4 w-4 mr-1" />
              {person.confidence}% match
            </div>
          </div>
          <div className="mb-2">
            {cardSubtitle && (
              <p className="text-sm text-gray-700 mb-1 line-clamp-3">{cardSubtitle}</p>
            )}
            <p className="text-sm text-gray-500">{person.riskDescription}</p>
            {person.profileNotes && person.matchKind !== 'pep_group' && (
              <p className="text-xs text-amber-800 mt-1 bg-amber-50 border border-amber-100 rounded p-2 line-clamp-2">
                {sanitizeVendorBranding(person.profileNotes)}
              </p>
            )}
            {Array.isArray(person.aliases) && person.aliases.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">Aliases: {person.aliases.join(', ')}</p>
            )}
            {person.threatLevel && (
              <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded border ${getThreatLevelColor(threatDisplay)}`}>
                {threatDisplay}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            type="button"
            onClick={() => onViewDetails?.(person)}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            Details
          </button>
          <button
            type="button"
            onClick={() => onDismiss?.(person)}
            className="px-3 py-1 text-sm border border-gray-300 text-gray-700 bg-white rounded hover:bg-gray-50 flex items-center justify-center"
            title="Hide this match from the list for this search"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}
